import { useState, useMemo, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Loader2,
  Grid3X3,
  Map,
  Heart,
  Bookmark,
  RefreshCw,
} from "lucide-react";
import { mockProperties, MockProperty } from "@/data/mockProperties";
import { PropertyCard } from "@/components/find/PropertyCard";
import {
  PropertyFiltersBar,
  PropertyFilters,
  SortOption,
} from "@/components/find/PropertyFilters";
import { PropertyDetailPage } from "@/components/find/detail/PropertyDetailPage";
import { InteractivePropertyMap } from "@/components/find/InteractivePropertyMap";
import { PropertyComparisonBar } from "@/components/find/PropertyComparisonBar";
import { PropertyComparisonDrawer } from "@/components/find/PropertyComparisonDrawer";
import { SavedPropertiesDrawer } from "@/components/find/SavedPropertiesDrawer";
import { PropertyComparisonProvider } from "@/hooks/use-property-comparison";
import { useSavedProperties } from "@/hooks/use-saved-properties";
import { useWorkspace } from "@/hooks/use-workspace";
import { usePropertySearch } from "@/hooks/use-property-search";
import { AddressSuggestion } from "@/hooks/use-address-autocomplete";
import { useFindSearchParams } from "@/hooks/use-find-search-params";
import {
  areFiltersEqual,
  createEmptyFilters,
  fromSearchParams,
  toDemoSearchParams,
  toSearchParams,
} from "@/lib/property-search-params";
import { toast } from "sonner";

export default function Find() {
  const { isLoading: workspaceLoading } = useWorkspace();

  // URL params → this session's last search → the default location.
  const { committedFilters, sortBy, searchParamsKey, setSearchParams } =
    useFindSearchParams();
  const isLiveSearch = committedFilters !== null;

  const {
    properties: apiProperties,
    isLoading: searchLoading,
    isLoadingMore,
    isRefreshing,
    error: searchError,
    total,
    hasMore,
    loadMore,
    refresh,
  } = usePropertySearch(committedFilters);

  // Draft filters are what's in the inputs. They only reach the API when the
  // user presses Search, which is what promotes them into the URL above.
  const [draftFilters, setDraftFilters] = useState<PropertyFilters>(
    () =>
      fromSearchParams(new URLSearchParams(searchParamsKey)).filters ??
      createEmptyFilters(),
  );
  const [selectedProperty, setSelectedProperty] = useState<MockProperty | null>(
    null,
  );
  const [viewMode, setViewMode] = useState<"grid" | "map">("grid");
  const [comparisonDrawerOpen, setComparisonDrawerOpen] = useState(false);
  const [savedDrawerOpen, setSavedDrawerOpen] = useState(false);
  /** Params of the live search we left for demo data, so we can restore it. */
  const [preDemoParams, setPreDemoParams] = useState<string | null>(null);
  const { savedProperties } = useSavedProperties();
  const favouriteProperties = savedProperties.filter((p) => !p.pipeline_id);

  // Pull the inputs back in sync when the committed search changes from outside
  // the page — browser back/forward, or a restored/shared link. Typing in the
  // inputs doesn't touch the URL, so this never clobbers in-progress edits.
  useEffect(() => {
    const { filters } = fromSearchParams(new URLSearchParams(searchParamsKey));
    if (filters) {
      setDraftFilters(filters);
    }
  }, [searchParamsKey]);

  const hasUnappliedFilters =
    isLiveSearch && !areFiltersEqual(draftFilters, committedFilters);

  /** Promote the draft filters into the URL — the only thing that triggers a fetch. */
  const commitSearch = (filters: PropertyFilters) => {
    setPreDemoParams(null);
    setSearchParams(toSearchParams(filters, sortBy));
  };

  const handleSearch = () => {
    if (draftFilters.search.trim()) {
      commitSearch(draftFilters);
    } else {
      // Leave the current results alone rather than silently jumping somewhere.
      toast.info("Enter a city, state, or zip code to search");
    }
  };

  // Picking an autocomplete suggestion is an explicit action, so it searches too.
  const handleSuggestionSelect = (suggestion: AddressSuggestion) => {
    const nextFilters = { ...draftFilters, search: suggestion.text };
    setDraftFilters(nextFilters);
    commitSearch(nextFilters);
  };

  // Sorting is client-side and deliberately not part of the query key, so
  // changing it re-orders the cached results without hitting the API.
  const handleSortChange = (nextSort: SortOption) => {
    const params = new URLSearchParams(searchParamsKey);
    if (nextSort === "newest") {
      params.delete("sort");
    } else {
      params.set("sort", nextSort);
    }
    setSearchParams(params, { replace: true });
  };

  // Filter mock properties based on current filters (for local/demo mode)
  const filteredMockProperties = useMemo(() => {
    return mockProperties.filter((property) => {
      // Search filter
      if (draftFilters.search) {
        const searchLower = draftFilters.search.toLowerCase();
        const matchesSearch =
          property.city.toLowerCase().includes(searchLower) ||
          property.state.toLowerCase().includes(searchLower) ||
          property.zip.includes(draftFilters.search) ||
          property.address.toLowerCase().includes(searchLower);
        if (!matchesSearch) return false;
      }

      // Price filters
      if (draftFilters.priceMin && property.price < draftFilters.priceMin)
        return false;
      if (draftFilters.priceMax && property.price > draftFilters.priceMax)
        return false;

      // Bedroom/bathroom filters
      if (draftFilters.bedsMin && property.bedrooms < draftFilters.bedsMin)
        return false;
      if (draftFilters.bathsMin && property.bathrooms < draftFilters.bathsMin)
        return false;

      // Property type filter
      if (
        draftFilters.propertyTypes.length > 0 &&
        !draftFilters.propertyTypes.includes(property.propertyType)
      ) {
        return false;
      }

      // Distress filters (OR logic - show if ANY checked distress type matches)
      const distressFiltersActive =
        draftFilters.foreclosure ||
        draftFilters.preForeclosure ||
        draftFilters.auction ||
        draftFilters.reo;
      if (distressFiltersActive) {
        const matchesDistress =
          (draftFilters.foreclosure && property.isForeclosure) ||
          (draftFilters.preForeclosure && property.isPreForeclosure) ||
          (draftFilters.auction && property.isAuction) ||
          (draftFilters.reo && property.isReo);
        if (!matchesDistress) return false;
      }

      // Financial metric filters
      if (draftFilters.capRateMin && property.capRate < draftFilters.capRateMin)
        return false;
      if (
        draftFilters.cashOnCashMin &&
        property.cashOnCash < draftFilters.cashOnCashMin
      )
        return false;
      if (
        draftFilters.grmMax &&
        property.grossRentMultiplier > draftFilters.grmMax
      )
        return false;

      // Deal indicator filters
      if (draftFilters.priceReduced && !property.priceReduction) return false;
      if (draftFilters.belowMarketValue && !property.belowMarketValue)
        return false;
      if (draftFilters.motivatedSeller && !property.motivatedSeller)
        return false;
      if (
        draftFilters.daysOnMarketMax &&
        property.daysOnMarket > draftFilters.daysOnMarketMax
      )
        return false;

      return true;
    });
  }, [draftFilters]);

  // Use API results when a search is committed, otherwise demo data
  const baseProperties = isLiveSearch ? apiProperties : filteredMockProperties;
  const resultCount = isLiveSearch ? total : filteredMockProperties.length;

  const displayProperties = useMemo(() => {
    const sorted = [...baseProperties];
    switch (sortBy) {
      case "price_low":
        sorted.sort((a, b) => a.price - b.price);
        break;
      case "price_high":
        sorted.sort((a, b) => b.price - a.price);
        break;
      case "cap_rate":
        sorted.sort((a, b) => (b.capRate ?? 0) - (a.capRate ?? 0));
        break;
      case "coc":
        sorted.sort((a, b) => (b.cashOnCash ?? 0) - (a.cashOnCash ?? 0));
        break;
      case "days_high":
        sorted.sort((a, b) => (b.daysOnMarket ?? 0) - (a.daysOnMarket ?? 0));
        break;
      case "days_low":
        sorted.sort((a, b) => (a.daysOnMarket ?? 0) - (b.daysOnMarket ?? 0));
        break;
      case "newest":
      default:
        sorted.sort((a, b) => (a.daysOnMarket ?? 0) - (b.daysOnMarket ?? 0));
        break;
    }
    return sorted;
  }, [baseProperties, sortBy]);

  const handlePropertyClick = (property: MockProperty) => {
    setSelectedProperty(property);
  };

  /** Drop every filter but keep the location — and stay in whichever mode we're in. */
  const clearFilters = () => {
    if (committedFilters) {
      const cleared = {
        ...createEmptyFilters(),
        search: committedFilters.search,
      };
      setDraftFilters(cleared);
      commitSearch(cleared);
    } else {
      setDraftFilters(createEmptyFilters());
    }
  };

  /** Save the current live search, then show demo data (cleared filters) */
  const switchToDemoData = () => {
    setPreDemoParams(searchParamsKey);
    setDraftFilters(createEmptyFilters());
    setSearchParams(toDemoSearchParams(sortBy));
  };

  /**
   * Leave demo mode. Restores the search we came from, or the default one if the
   * user arrived on a `?demo=1` link directly. Either way it's served from cache.
   */
  const exitDemoMode = () => {
    setSearchParams(new URLSearchParams(preDemoParams ?? ""));
    setPreDemoParams(null);
  };

  if (workspaceLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // When a property is selected, swap the search header/grid for the
  // in-place detail page (matches the mockup's `body.detail-open` behavior).
  if (selectedProperty) {
    return (
      <PropertyComparisonProvider>
        <div className="flex flex-col h-[calc(100vh-4rem)] overflow-y-auto">
          <PropertyDetailPage
            property={selectedProperty}
            onBack={() => setSelectedProperty(null)}
          />
        </div>
      </PropertyComparisonProvider>
    );
  }

  return (
    <PropertyComparisonProvider>
      <div className="flex flex-col h-[calc(100vh-4rem)]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border bg-background">
          <div>
            <h1 className="text-2xl font-bold text-foreground">
              Find Properties
            </h1>
            <p className="text-sm text-muted-foreground">
              {isLiveSearch
                ? `${resultCount.toLocaleString()} properties found via live MLS data`
                : `${resultCount.toLocaleString()} sample properties (demo data)`}
            </p>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            {/* Saved Properties */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSavedDrawerOpen(true)}
              className="gap-2"
            >
              <Heart className="w-4 h-4" aria-hidden />
              Saved Properties
              {favouriteProperties.length > 0 && (
                <span className="bg-destructive text-destructive-foreground text-xs px-1.5 py-0.5 rounded-full font-bold">
                  {favouriteProperties.length}
                </span>
              )}
            </Button>

            {/* Saved Searches (placeholder count) */}
            <Button
              variant="outline"
              size="sm"
              className="gap-2"
              disabled
              title="Saved searches — coming soon"
            >
              <Bookmark className="w-4 h-4" aria-hidden />
              Saved Searches
            </Button>

            {/* View Toggle */}
            <Tabs
              value={viewMode}
              onValueChange={(v) => setViewMode(v as "grid" | "map")}
            >
              <TabsList>
                <TabsTrigger value="grid" className="gap-2">
                  <Grid3X3 className="w-4 h-4" />
                  Grid
                </TabsTrigger>
                <TabsTrigger value="map" className="gap-2">
                  <Map className="w-4 h-4" />
                  Map
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </div>

        {/* Search row (inline Search Properties + Filters) + demo-data toggle */}
        <div className="flex flex-col gap-2 border-b border-border bg-card">
          <PropertyFiltersBar
            filters={draftFilters}
            onFiltersChange={setDraftFilters}
            onSuggestionSelect={handleSuggestionSelect}
            onSearch={handleSearch}
            searchLoading={searchLoading}
            resultCount={displayProperties.length}
            sortBy={sortBy}
            onSortChange={handleSortChange}
          />
          <div className="flex items-center gap-2 px-4 pb-3">
            {isLiveSearch ? (
              <>
                <Button variant="ghost" size="sm" onClick={switchToDemoData}>
                  Switch to Demo Data
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="gap-2"
                  onClick={() => refresh()}
                  disabled={searchLoading || isRefreshing}
                >
                  <RefreshCw
                    className={isRefreshing ? "w-4 h-4 animate-spin" : "w-4 h-4"}
                    aria-hidden
                  />
                  {isRefreshing ? "Refreshing…" : "Refresh results"}
                </Button>
                {hasUnappliedFilters && (
                  <span className="text-sm text-muted-foreground">
                    Filters changed — press Search Properties to apply
                  </span>
                )}
              </>
            ) : (
              <>
                <Button variant="ghost" size="sm" onClick={exitDemoMode}>
                  Back to search results
                </Button>
                <span className="text-sm text-muted-foreground">
                  Showing sample data — not live listings
                </span>
              </>
            )}
            {searchError && (
              <span className="text-sm text-destructive">{searchError}</span>
            )}
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-hidden pb-16">
          {viewMode === "grid" ? (
            <ScrollArea className="h-full">
              <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {searchLoading && displayProperties.length === 0 ? (
                  <div className="col-span-full flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-primary" />
                  </div>
                ) : displayProperties.length === 0 ? (
                  <div className="col-span-full text-center py-12">
                    <p className="text-muted-foreground">
                      No properties match your filters
                    </p>
                    <Button variant="link" onClick={clearFilters}>
                      Clear all filters
                    </Button>
                  </div>
                ) : (
                  <>
                    {/* Scroll callout when more properties available */}
                    {isLiveSearch && hasMore && (
                      <div className="col-span-full bg-primary/10 border border-primary/20 rounded-lg p-3 text-center">
                        <p className="text-sm text-primary font-medium">
                          Showing {displayProperties.length} of{" "}
                          {total.toLocaleString()} properties — scroll down and
                          click "Load More" to see additional results
                        </p>
                      </div>
                    )}

                    {displayProperties.map((property) => (
                      <PropertyCard
                        key={property.id}
                        property={property}
                        isSelected={selectedProperty?.id === property.id}
                        onClick={() => handlePropertyClick(property)}
                      />
                    ))}

                    {/* Loading More Indicator */}
                    {isLoadingMore && (
                      <div className="col-span-full flex items-center justify-center py-4 gap-2">
                        <Loader2 className="w-5 h-5 animate-spin text-primary" />
                        <span className="text-sm text-muted-foreground">
                          Loading more properties...
                        </span>
                      </div>
                    )}

                    {/* Load More Button */}
                    {isLiveSearch && hasMore && !isLoadingMore && (
                      <div className="col-span-full flex justify-center py-4">
                        <Button
                          variant="outline"
                          onClick={() => loadMore()}
                          disabled={searchLoading}
                        >
                          {searchLoading ? (
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          ) : null}
                          Load More Properties
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </ScrollArea>
          ) : (
            <InteractivePropertyMap
              properties={displayProperties}
              selectedProperty={selectedProperty}
              onPropertySelect={handlePropertyClick}
              className="h-full"
            />
          )}
        </div>

        {/* Comparison Bar */}
        <PropertyComparisonBar
          onCompare={() => setComparisonDrawerOpen(true)}
        />

        {/* Comparison Drawer */}
        <PropertyComparisonDrawer
          open={comparisonDrawerOpen}
          onOpenChange={setComparisonDrawerOpen}
        />

        {/* Saved Properties Drawer */}
        <SavedPropertiesDrawer
          open={savedDrawerOpen}
          onOpenChange={setSavedDrawerOpen}
          onPropertyClick={(property) => {
            setSelectedProperty(property);
            setSavedDrawerOpen(false);
          }}
        />
      </div>
    </PropertyComparisonProvider>
  );
}
